import { AfterViewInit, Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as _ from 'lodash';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconDirective, NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { getDisplayUnit } from 'src/app/shared/utils/utils';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { ModalEditPackagingFromFinalProductComponent } from '../modal-edit-packaging-from-final-product/modal-edit-packaging-from-final-product.component';
import { ModalEditRawMaterialFromFinalProductComponent } from '../modal-edit-raw-material-from-final-product/modal-edit-raw-material-from-final-product.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { FinalProductService } from 'src/app/private/services/final-product.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-modal-traceability-by-order-process',
    standalone: true,
    imports: [
        NzModalFooterDirective,
        NzTagModule,
        NzTableModule,
        FormsModule,
        ReactiveFormsModule,
        NzEmptyModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzModalTitleDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzMessageModule,
        NzInputDirective,
        NzInputGroupComponent,
        NzIconDirective,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule,
        DecimalPipe
    ],
    templateUrl: './modal-traceability-by-order-process.component.html',
    styleUrl: './modal-traceability-by-order-process.component.scss'
})
export class ModalTraceabilityByOrderProcessComponent implements OnInit, AfterViewInit{
    @Output() emitService = new EventEmitter();
    @ViewChild('modalContent') private readonly _modalContent!: ElementRef;

    isLoading: boolean = true;
    isSaving: boolean = false;

    productionOrderProcess!: ProductionOrderProcessDto;
    finalProducts: FinalProductDto[] = [];
    finalProductsFiltered: FinalProductDto[] = [];
    rawMaterials: RawMaterialDto[] = [];
    packagingsByFinalProductId: Map<number, PackagingDto[]> = new Map();
    tableHeight!: number;
    data = inject(NZ_MODAL_DATA);
    searchFinalProduct: string = '';
    searchPackaging: Record<number, string> = {};
    searchRawMaterial: Record<number, string> = {};
    searchRawMaterialInPackaging: Record<string, string> = {};
    rawMaterialOfArticlesSelected: Record<number, RawMaterialDto[]> = {};
    expandSetFinalProduct = new Set<number>();
    expandSetPackage = new Set<number>();
    openedFromFinaliseProcess: boolean = false;

    private readonly toast = inject(NzMessageService);
    private readonly modalService = inject(NzModalService);
    private readonly finalProductService = inject(FinalProductService);

    /**
     * Devuelve la unidad de medida a mostrar según el proceso de producción
     */
    getDisplayUnit(): string {
        return getDisplayUnit(
            this.productionOrderProcess?.productionProcessId,
            this.productionOrderProcess?.productionProcess?.name,
            this.finalProducts?.[0]?.article?.measurementUnit?.abbreviation || ''
        );
    }

    async ngOnInit() {

        this.productionOrderProcess = this.data?.productionOrderProcess;
        this.openedFromFinaliseProcess = this.data?.openedFromFinaliseProcess ? this.data?.openedFromFinaliseProcess : false;

        if (!this.productionOrderProcess) {
            this.toast.error('No se ha encontrado el proceso de orden de producción');
            return;
        }

        if (!this.productionOrderProcess.productionProcessId) {
            this.toast.error('No se ha proporcionado el ID del proceso de producción');
            return;
        }

        this.finalProducts = this.data?.finalProducts;
        this.finalProductsFiltered = _.cloneDeep(this.data?.finalProducts);

        if (this.finalProducts) {
            await this.loadPackagingsOfFinalProducts();
        } else {
            this.toast.error('No se han encontrado productos finales');
        }

        this.rawMaterials = this.data?.rawMaterials;

        if (!this.rawMaterials) {
            this.toast.error('No se han encontrado materias primas');
        }

        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        setTimeout(() => {
            this.tableHeight =
                (this._modalContent.nativeElement as HTMLElement)
                    .clientHeight - TITLE_TABLE_SIZE;
        });
    }

    getFinalProductPackagingsCount(finalProductId: number): number {
        return this.packagingsByFinalProductId.get(finalProductId)?.length || 0;
    }

    async loadPackagingsOfFinalProducts() {
        for (const finalProduct of this.finalProducts) {
            if(finalProduct.packagings){
                this.packagingsByFinalProductId.set(finalProduct.id!, finalProduct.packagings);
            }
        }
    }

    handleCancel() {
        this.emitService.emit(false);
    }

    // FILTERS EXPANDED TABLES

    onExpandFinalProductChange(id: number, checked: boolean): void {
        if (!checked) {
            this.expandSetFinalProduct.add(id);
        } else {
            this.expandSetFinalProduct.delete(id);
        }
    }

    onExpandPackageChange(id: number, checked: boolean): void {
        if (!checked) {
            this.expandSetPackage.add(id);
        } else {
            this.expandSetPackage.delete(id);
        }
    }

    onSearchFinalProduct(event: string) {
        try {
            this.expandSetPackage.clear();
            this.expandSetFinalProduct.clear();
            this.finalProductsFiltered = _.cloneDeep(this.finalProducts.filter(finalProduct => finalProduct.article?.name?.toLowerCase().includes(event.toLowerCase()) || finalProduct.articleBatch?.batchNumber?.toLowerCase().includes(event.toLowerCase())));
        } catch (error) {
            this.toast.error(`Error al filtrar los productos finales`);
        }
    }

    async clearFilterFinalProduct(): Promise<void> {
        this.expandSetPackage.clear();
        this.expandSetFinalProduct.clear();
        this.searchFinalProduct = '';
        this.finalProductsFiltered = this.finalProducts;
    }

    onSearchPackaging(event: string, idComponent: number) {
        try {
            this.expandSetPackage.clear();
            this.finalProductsFiltered[idComponent].packagings = _.cloneDeep(this.finalProducts[idComponent].packagings?.filter(packaging => packaging.packagingType?.name?.toLowerCase().includes(event.toLowerCase()) || packaging.batchNumber?.toLowerCase().includes(event.toLowerCase()) || packaging.externalBatchNumber?.toLowerCase().includes(event.toLowerCase())));
        } catch (error) {
            this.toast.error(`Error al filtrar los formatos del artículo ${this.finalProducts[idComponent].article?.name}`);
        }
    }

    async clearFilterPackaging(idComponent: number): Promise<void> {
        this.expandSetPackage.clear();
        this.searchPackaging[idComponent] = '';
        this.finalProductsFiltered[idComponent].packagings = this.finalProducts[idComponent].packagings;
        this.onSearchPackaging(this.searchPackaging[idComponent], idComponent);
    }

    onSearchRawMaterial(event: string, idComponent: number) {
        try {
            this.finalProductsFiltered[idComponent].rawMaterials = _.cloneDeep(this.finalProducts[idComponent].rawMaterials?.filter(rawMaterial => rawMaterial.article?.name?.toLowerCase().includes(event.toLowerCase())));
        } catch (error) {
            this.toast.error(`Error al filtrar las materias primas del artículo ${this.finalProducts[idComponent].article?.name}`);
        }
    }

    async clearFilterRawMaterial(idComponent: number): Promise<void> {
        this.searchRawMaterial[idComponent] = '';
        this.onSearchRawMaterial(this.searchRawMaterial[idComponent], idComponent);
    }

    onSearchRawMaterialInPackaging(event: string, idFinalProductComponent: number, idComponent: number) {
        try {
            this.finalProductsFiltered[idFinalProductComponent].packagings![idComponent]!.rawMaterials = this.finalProducts[idFinalProductComponent].packagings![idComponent].rawMaterials?.filter(rawMaterial => rawMaterial.article?.name?.toLowerCase().includes(event.toLowerCase()));
        } catch (error) {
            this.toast.error(`Error al filtrar las materias primas del packaging ${this.finalProducts[idFinalProductComponent].packagings![idComponent].batchNumber}`);
        }
    }

    async clearFilterRawMaterialInPackaging(idFinalProductComponent: number, idComponent: number): Promise<void> {
        this.searchRawMaterialInPackaging[idFinalProductComponent + '_' + idComponent] = '';
        this.onSearchRawMaterialInPackaging(this.searchRawMaterialInPackaging[idFinalProductComponent + '_' + idComponent], idFinalProductComponent, idComponent);
    }

    // ACTIONS TABLE

    async editRawMaterialFromFinalProduct(rawMaterial: RawMaterialDto) {
        const modal = this.modalService.create({
            nzContent: ModalEditRawMaterialFromFinalProductComponent,
            nzStyle: { width: '70%', top: '20%' },
            nzBodyStyle: { height: '30vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Editar cantidad de la materia prima ${rawMaterial.article?.name} ${rawMaterial.articleBatch?.batchNumber ? rawMaterial.articleBatch?.batchNumber : ''}`,
                id: rawMaterial.id,
                rawMaterial: rawMaterial
            }
        });

        modal.componentInstance?.emitService.subscribe(rawMaterialUpdated => {
            modal.close();
            rawMaterial.rawQuantity = rawMaterialUpdated.quantity;
        });
    }

    async editPackagingFromFinalProduct(packaging: PackagingDto, finalProductId:number) {
        const modal = this.modalService.create({
            nzContent: ModalEditPackagingFromFinalProductComponent,
            nzStyle: { width: '70%', top: '20%' },
            nzBodyStyle: { height: '30vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Editar cantidad del formato ${packaging.packagingType?.name}`,
                id: packaging.id,
                packaging: packaging
            }
        });

        modal.componentInstance?.emitService.subscribe(packagingUpdated => {
            modal.close();
            packaging.quantity = packagingUpdated.quantity;
            this.finalProducts.find(finalProduct => finalProduct.id === finalProductId)!.packagings!.find(packagingInFinalProduct => packagingInFinalProduct.id === packaging.id)!.quantity = packagingUpdated.quantity;
        });
    }

    async updateStock() {
        try{
            this.isSaving = true;
            // TODO: refactorizar cuando se refactorice el endpoint de actualizar stock por proceso
            // await this.finalProductService.updateStock(this.finalProducts);

            this.isSaving = false;
            this.emitService.emit(true);
        } catch (error) {
            this.toast.error(`Error al actualizar los stocks de los productos del proceso: ${this.productionOrderProcess?.description}`);
        }
    }

    trackByIndexPackaging(_: number, data: PackagingDto): number {
        return data.id!;
    }
}

